import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Растянуть на всю ширину — основной вид кнопки на мобильных формах. */
  block?: boolean;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-contrast shadow-brand hover:bg-brand-hover active:brightness-95',
  secondary:
    'bg-surface-2 text-content border border-border hover:bg-surface-3 active:brightness-95',
  soft: 'bg-brand-soft text-brand-soft-text hover:brightness-105 active:brightness-95',
  ghost: 'text-muted hover:bg-surface-2 hover:text-content active:bg-surface-3',
  danger: 'bg-danger text-white hover:brightness-110 active:brightness-95',
};

const SIZES: Record<ButtonSize, string> = {
  // Минимум 44px по высоте — рекомендованный размер зоны касания. Маленькая
  // кнопка ниже, поэтому добирает недостающее невидимым расширением.
  sm: 'h-9 px-3 text-sm gap-1.5 rounded-sm tap-target',
  md: 'h-11 px-4 text-[15px] gap-2 rounded-md',
  lg: 'h-13 px-5 text-base gap-2 rounded-lg',
};

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  loading = false,
  iconLeft,
  iconRight,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium select-none',
        'transition-[background-color,transform,filter,box-shadow] duration-150',
        // Лёгкое «вдавливание» при нажатии — тактильная обратная связь без вибрации
        'active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <>
          <Spinner />
          {/* Подпись остаётся в разметке скрытой: иначе на время загрузки
              кнопка теряет доступное имя и перестаёт быть различимой для
              скринридера и для поиска по имени в тестах. */}
          <span className="sr-only">{children}</span>
        </>
      ) : (
        <>
          {iconLeft}
          {children}
          {iconRight}
        </>
      )}
    </button>
  );
}

function Spinner() {
  return (
    <span
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden
    />
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Обязателен: кнопка без текста непонятна скринридеру. */
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function IconButton({
  label,
  variant = 'ghost',
  size = 'md',
  className,
  children,
  type = 'button',
  ...rest
}: IconButtonProps) {
  // Маленькая круглая кнопка — 36 пикселей: вид сохраняется, зона касания
  // добирается до сорока четырёх невидимым расширением.
  const sizeClass = { sm: 'size-9 tap-target', md: 'size-11', lg: 'size-13' }[size];

  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        'transition-[background-color,transform] duration-150 active:scale-90',
        'disabled:pointer-events-none disabled:opacity-40',
        VARIANTS[variant],
        sizeClass,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
